import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { Title } from '../../shared/styled';
import { Column, FinanceTable } from '../../components/FinanceTable';
import {
    Transaction,
    TransactionStatus,
    formatTransactionDate,
    formatTransactionTime,
    maskPaymentDetails,
    formatCurrency
} from '../../components/transactionTypes';
import MessageQuestionIcon from "assets/message-question.svg";

interface WithdrawalHistoryProps {
    transactions?: Transaction[];
}

const STATUS_TOOLTIPS: Record<TransactionStatus, string> = {
    completed: 'Action has been completed',
    pending: 'Action is still in progress',
    failed: 'Action has failed',
    refunded: 'Action has been refunded',
    canceled: 'Action has been canceled',
};

export const WithdrawalHistory: React.FC<WithdrawalHistoryProps> = ({ transactions = [] }) => {
    const [tooltipVisible, setTooltipVisible] = useState<string | null>(null);
    const tooltipRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const [tooltipPosition, setTooltipPosition] = useState<{ top: number } | null>(null);

    const handleInfoClick = (transactionId: string, e: React.MouseEvent) => {
        e.stopPropagation();

        // Calculate position for mobile
        if (window.innerWidth <= 768) {
            const target = e.currentTarget as HTMLElement;
            const rect = target.getBoundingClientRect();
            const viewportHeight = window.innerHeight;

            let topPosition = rect.bottom + 12;
            const estimatedTooltipHeight = 80;

            // If would go off bottom, position above
            if (topPosition + estimatedTooltipHeight > viewportHeight - 20) {
                topPosition = rect.top - estimatedTooltipHeight - 12;

                // If still not enough space, center it
                if (topPosition < 20) {
                    topPosition = viewportHeight / 2 - estimatedTooltipHeight / 2;
                }
            }

            setTooltipPosition({ top: topPosition });
        }

        // Toggle tooltip - close if already open, open if closed
        setTooltipVisible(prev => prev === transactionId ? null : transactionId);
    };

    // Close tooltip when clicking outside
    useEffect(() => {
        if (!tooltipVisible) return;

        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            const target = event.target as HTMLElement;
            const tooltipElement = tooltipRefs.current.get(tooltipVisible);

            // Check if click is outside the tooltip and its trigger icon
            if (tooltipElement && !tooltipElement.contains(target)) {
                // Also check if we're not clicking the icon itself
                const iconWrapper = tooltipElement.parentElement;
                if (iconWrapper && !iconWrapper.contains(target)) {
                    setTooltipVisible(null);
                }
            }
        };

        // Add a small delay to prevent immediate closing when opening
        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }, 100);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [tooltipVisible]);

    const columns: Column<Transaction>[] = [
        {
            key: 'datetime',
            title: 'Date & Time',
            mobileLabel: 'Date & Time',
            render: (data: Transaction) => (
                <DateTimeCell>
                    <DateText>{formatTransactionDate(data.date)}</DateText>
                    <TimeText>{formatTransactionTime(data.date)}</TimeText>
                </DateTimeCell>
            ),
            flex: '1',
        },
        {
            key: 'operation',
            title: 'Operation',
            mobileLabel: 'Operation',
            render: (data: Transaction) => (
                <OperationCell>
                    <OperationName>Withdrawal</OperationName>
                    <PaymentDetails>{maskPaymentDetails(data.paymentMethod, data.cardLast4)}</PaymentDetails>
                </OperationCell>
            ),
            flex: '1.5',
        },
        {
            key: 'amount',
            title: 'Amount',
            mobileLabel: 'Amount',
            render: (data: Transaction) => <AmountText>{formatCurrency(data.amount)}</AmountText>,
            flex: '0.8',
        },
        {
            key: 'status',
            title: 'Status',
            mobileLabel: 'Status',
            render: (data: Transaction) => (
                <StatusCell>
                    <StatusBadge status={data.status}>
                        {data.status.charAt(0).toUpperCase() + data.status.slice(1)}
                    </StatusBadge>
                    <InfoIconWrapper>
                        <InfoIcon onClick={(e) => handleInfoClick(data.id, e)}>
                            <MessageQuestionIcon />
                        </InfoIcon>
                        {tooltipVisible === data.id && (
                            <StatusTooltip
                                ref={(el) => {
                                    if (el) {
                                        tooltipRefs.current.set(data.id, el);
                                    } else {
                                        tooltipRefs.current.delete(data.id);
                                    }
                                }}
                                mobileTop={tooltipPosition?.top}
                            >
                                <TooltipText>
                                    {STATUS_TOOLTIPS[data.status] || 'Status information not available.'}
                                </TooltipText>
                            </StatusTooltip>
                        )}
                    </InfoIconWrapper>
                </StatusCell>
            ),
            flex: '1.2',
        },
        {
            key: 'details',
            title: 'Details',
            mobileLabel: 'Details',
            render: (data: Transaction) => (
                <DetailsText>{data.details || "Withdrawal to an individual's bank account"}</DetailsText>
            ),
            flex: '1.5',
        },
    ];

    return (
        <Container>
            <HeaderWrapper>
                <Title>
                    <FormattedMessage id="Finance.Withdrawal.History" defaultMessage="Withdrawal History" />
                </Title>
            </HeaderWrapper>

            {transactions.length === 0 ? (
                <EmptyState>
                    <FormattedMessage
                        id="Finance.Withdrawal.NoHistory"
                        defaultMessage="You haven't made any withdrawals yet"
                    />
                </EmptyState>
            ) : (
                <TableWrapper>
                    <FinanceTable
                        columns={columns}
                        data={transactions}
                        keyExtractor={(item) => item.id}
                        emptyMessage="No withdrawals found"
                        mobileCardView={true}
                    />
                </TableWrapper>
            )}
        </Container>
    );
};

const slideIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const slideInDesktop = keyframes`
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const Container = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    @media (max-width: 1024px) { min-height: 200px; }
    @media (max-width: 768px) { min-height: 100px; gap: 12px; }
`;

const HeaderWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const EmptyState = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    min-height: 150px;
    color: #A0A0A0;
    font-size: 13px;
    font-weight: 400;
    font-family: SF Pro Display, sans-serif;
    @media (max-width: 768px) {
        padding: 60px 20px;
        font-size: 12px;
    }
`;

const TableWrapper = styled.div`
    width: 100%;
`;

const DateTimeCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const DateText = styled.span`
    font-family: SF Pro Display, sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #000000;
    line-height: 18px;
`;

const TimeText = styled.span`
    font-family: SF Pro Display, sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: #00000080;
    line-height: 18px;
`;

const OperationCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const OperationName = styled.span`
    font-family: SF Pro Display, sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #000000;
    line-height: 18px;
`;

const PaymentDetails = styled.span`
    font-family: SF Pro Display, sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: #00000080;
    line-height: 18px;
`;

const AmountText = styled.span`
    font-family: SF Pro Display, sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #000000;
    line-height: 18px;
`;

const StatusCell = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const StatusBadge = styled.div<{ status: TransactionStatus }>`
    border-radius: 6px;
    font-family: SF Pro Display, sans-serif;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    width: 90px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${props => {
        switch(props.status) {
            case 'completed':
                return '#87FF9B';
            case 'pending':
                return '#fddf86';
            case 'failed':
                return '#FF4C4C';
            case 'refunded':
                return '#6CCFF6';
            case 'canceled':
                return '#FF8F8F';
            default:
                return '#E9E9E9';
        }
    }};
    color: #000000;
`;

const InfoIconWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const InfoIcon = styled.div`
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: 0.5;
    transition: opacity 0.2s ease;
    &:hover { opacity: 1; }
    svg {
        width: 20px;
        height: 20px;
    }
`;

const StatusTooltip = styled.div<{ mobileTop?: number }>`
    position: absolute;
    bottom: calc(100% + 12px);
    left: 50%;
    transform: translateX(-50%);

    background: #E2E5FF;
    border: 1px solid #cdd2f6;
    border-radius: 12px;
    padding: 14px 16px;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    max-width: 280px;
    min-width: 200px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 100;

    animation: ${slideInDesktop} 0.3s ease-out forwards;

    &::after,
    &::before {
        content: none !important;
        display: none !important;
        border: none !important;
        width: 0 !important;
        height: 0 !important;
    }

    @media (max-width: 768px) {
        position: fixed;
        left: 16px;
        right: 16px;
        top: ${({ mobileTop }) => mobileTop ? `${mobileTop}px` : '50%'};
        transform: ${({ mobileTop }) => mobileTop ? 'none' : 'translateY(-50%)'};
    }
`;

const TooltipText = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: #000000;
    line-height: 1.5;
    font-family: SF Pro Display, sans-serif;

    @media (max-width: 768px) {
        font-size: 12px;
    }
`;

const DetailsText = styled.span`
    font-family: SF Pro Display, sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: #000000;
    line-height: 18px;
`;